import React, { useCallback } from 'react';

import {
  AdminFonctionnelControllerApi,
  ConditionsReactivateMotifMefDto,
  MotifMefDto,
  MotifMefRequestDto,
  ReferentielSearchResultDtoMotifMefDto,
} from 'lib_api/lib/api/gen';

import {
  FetchDataResult,
  TableHeaderFunctionWithoutSort,
  TablePagination,
  TableSort,
} from 'components/WrappedComponents/Table/types';
import { useApi } from 'hooks/ApiStoreContext';
import { MotifMefFormValues } from 'types/referentiels/MotifMef';

import DashboardHeader from './DashboardHeader/DashboardHeader';
import { FilterDashboardMotifMef, MotifMefSort } from './types';

export const buildHeader: TableHeaderFunctionWithoutSort<
  MotifMefDto,
  FilterDashboardMotifMef
> = (fetchedData, _selectedData, filter, setFilter) => (
  <DashboardHeader
    totalCount={fetchedData?.total ?? 0}
    filter={filter}
    setFilter={setFilter}
  />
);

export function useSearchMotifMef(
  filter: FilterDashboardMotifMef,
  sort: TableSort<MotifMefSort>,
  pagination: TablePagination,
): [
  () => Promise<ReferentielSearchResultDtoMotifMefDto>,
  (
    result: ReferentielSearchResultDtoMotifMefDto,
  ) => FetchDataResult<MotifMefDto>,
] {
  const controller = useApi().ReferentielControllerApi;

  return [
    () => {
      return controller.searchMotifMefUsingSpecsUsingGET({
        filters: {
          ...filter,
          page: pagination.page,
          pageSize: pagination.pageSize,
        },
        sorts: {
          libelle: sort?.LIBELLE,
          natureMef: sort?.NATURE,
        },
      });
    },
    result => {
      return {
        data: result.results.referenceDtoList,
        total: result.total,
      };
    },
  ];
}

export const buildFormValues = (motifMef: MotifMefDto): MotifMefFormValues => {
  return {
    motifMef: motifMef?.motifMef ?? undefined,
    nature: motifMef?.nature ?? undefined,
    branche: motifMef?.branche?.idCorrelation ?? undefined,
    dicem: motifMef?.dicem ?? undefined,
    rodeo: motifMef?.rodeo ?? undefined,
  };
};

export const buildRequestDto = (
  formValues: MotifMefFormValues,
): MotifMefRequestDto => {
  return {
    motifMef: formValues?.motifMef?.trim() || null,
    nature: formValues?.nature || null,
    brancheIdCorrelation: formValues?.branche || null,
    dicem: formValues?.dicem || null,
    rodeo: formValues?.rodeo || null,
  };
};

export function useChangeValidityMotifMef(
  controller: AdminFonctionnelControllerApi,
): [
  (idCorrelation: string) => Promise<Response>,
  (idCorrelation: string) => Promise<Response>,
  (idCorrelation: string) => Promise<ConditionsReactivateMotifMefDto>,
] {
  const submitDeprecateReferentiel = useCallback(
    (idCorrelation: string): Promise<Response> => {
      return controller.deprecateMotifMefUsingPUT(idCorrelation);
    },
    [controller],
  );

  const submitReactivateReferentiel = useCallback(
    (idCorrelation: string): Promise<Response> => {
      return controller.reactivateMotifMefUsingPUT(idCorrelation);
    },
    [controller],
  );

  const conditionsReactivateReferentiel = useCallback(
    (idCorrelation: string): Promise<ConditionsReactivateMotifMefDto> => {
      return controller.conditionReactivationMotifMefUsingGET(idCorrelation);
    },
    [controller],
  );

  return [
    submitReactivateReferentiel,
    submitDeprecateReferentiel,
    conditionsReactivateReferentiel,
  ];
}
